import DiagnosticMonitorPage from "../DiagnosticMonitorPage";
import { DEMO_PRESET } from "../hooks/useDemoData";
import { DemoQueryClientProvider } from "./DemoQueryClientProvider";
import { DataGridSettingsProvider } from "@/components/DataGridSettingsProvider";
import { ModalProvider } from "@/components/ModalProvider";
import { PresetProvider } from "@/components/PresetProvider";
import { isViewModeActive, ViewProvider } from "@/components/ViewProvider";
import AddInIds from "@/constants/AddInIds";
import { PageType } from "@/constants/PageType";
import { useAddinData } from "@/hooks/addins/useAddinData";
import { useDiagnosticViews } from "@/hooks/views/useDiagnosticViews";
import { useViewMigration } from "@/hooks/views/useViewMigration";
import { Preset } from "@/models/Preset";
import isDemo from "@/utilities/isDemo";

function ViewModeShell({ pageType, children }: { pageType: PageType; children: React.ReactNode }) {
  return (
    <PresetProvider scope={`${pageType}.preset`}>
      <ViewProvider pageType={pageType}>
        <DataGridSettingsProvider pageType={pageType}>
          <ModalProvider>{children}</ModalProvider>
        </DataGridSettingsProvider>
      </ViewProvider>
    </PresetProvider>
  );
}

function LegacyShell({ pageType, children }: { pageType: PageType; children: React.ReactNode }) {
  return (
    <PresetProvider
      scope={`${pageType}.preset`}
      initialPresets={isDemo ? [DEMO_PRESET] : undefined}
    >
      <DataGridSettingsProvider pageType={pageType}>
        <ModalProvider>{children}</ModalProvider>
      </DataGridSettingsProvider>
    </PresetProvider>
  );
}

function ShellDispatcher({ pageType }: { pageType: PageType }) {
  useViewMigration();
  const { data: views = [], isLoading: viewsLoading } = useDiagnosticViews();
  const { data: presets = [], isLoading: presetsLoading } = useAddinData<Preset>(AddInIds.presets);

  if (viewsLoading || presetsLoading) return null;

  const migrationInFlight = sessionStorage.getItem("diagViewsMigrating") === "1";
  const migrationPending = views.length === 0 && presets.length > 0;

  if (migrationInFlight || migrationPending) return null;

  const Shell = isViewModeActive(views) ? ViewModeShell : LegacyShell;
  return (
    <Shell pageType={pageType}>
      <DiagnosticMonitorPage />
    </Shell>
  );
}

export function DiagnosticMonitorPageWrapper(props: { pageType: PageType }) {
  if (isDemo) {
    return (
      <DemoQueryClientProvider>
        <LegacyShell pageType={props.pageType}>
          <DiagnosticMonitorPage />
        </LegacyShell>
      </DemoQueryClientProvider>
    );
  }
  return <ShellDispatcher pageType={props.pageType} />;
}
